import store from "@/plugins/store";
import moment from 'moment';

function Claim() {
	// public
	this.id = 0;
	this.status = 0; // New
	this.number = '';
	this.description = 'New claim';
	this.remark = '';
	this.details = [];
	this.period = null;
	this.email = false;

	// private, accessed by accessors
	this._supplier = { id: 0, display_name: '' };
	this._country = { id: 0, code: '', display_name: '', tax_perc: 0.0 }
	this._date = new Date();
	this._submitted_date = null;
	this._currency = { id: 0, symbol: '', code: '', exrate: 1.0 };
	this._approval_by = { id: 0, display_name: '' };

	this.last_detail_id = 0;

	// Add a new detail to our expense claim
	this.add_detail = function() {
		// negative numbers for our temporary ids
		this.last_detail_id = this.last_detail_id - 1;

		// create our new detail
		let new_detail = {
			id: this.last_detail_id,
			description: '',
			receipt: {},
			job: { id: 0, code: '', display_name: '' },
			phase: { id: 0, code: '', display_name: '' },
			kind: { id: 0, code: '', display_name: '' },
			amount: 0.00, /* amount converted into our expense claim currency */
		};

		// yes, this makes claim available in our functions.. like magic... 
		let claim = this;

		new_detail.convert_currency = function() {
			let value = this.receipt.value;

			if (claim._currency.id != 0 && this.receipt.currency.id != claim._currency.id) {
				// TODO: convert amount
				console.log("Convert from ",this.receipt.currency.code, value, this.receipt.currency.exrate)

				value = (claim._currency.exrate * (value / this.receipt.currency.exrate)).toFixed(2);

				console.log("Converted to ",claim._currency.code, value, claim._currency.exrate)
			}

			this.amount = 1.0 * value;
		};

		// add our new detail
		this.details.push(new_detail);

		// return our new detail
		return new_detail;
	}

	// remove a detail from our expense claim
	this.rem_detail = function(detail_id) {
		for(let i = 0; i < this.details.length; i++) {
			if (this.details[i].id == detail_id) {
				this.details.splice(i, 1);
				return;
			}
		}
	}

	// this sets our data based on claim retrieved from API, we do this so we take info as it is
	this.set = function(claim) {
		// copy header data
		this.id = claim.id;
		this.number = claim.number;
		this.description = claim.description;
		this.status = claim.status;
		this.remark = claim.remark;
		this.period = claim.period;

		// we don't want these re-evaluated, just copy
		this._supplier = claim.supplier;
		this._country = claim.country;
		this._date = new Date(claim.date);
		if (claim.submitted_date) {
			this._submitted_date = new Date(claim.submitted_date);
		} else {
			this._submitted_date = null;
		}
		// TODO make sure exchange rate is added to our currency
		this._currency = claim.currency;
		// TODO only copy approval by if set else clear
		if (claim.approval_by) {
			this._approval_by = { id: claim.approval_by.id, display_name: claim.approval_by.display_name };
		} else {
			this._approval_by = { id: 0, display_name: "" };
		}

		// copy details anew
		this.details = [];
		claim.details.forEach(detail => {
			let new_detail = this.add_detail();

			// copy data
			new_detail.id = detail.id;
			new_detail.description = detail.description;

			new_detail.receipt = {
				id: detail.receipt.id,
				date: detail.receipt.date,
				date_fmt: moment(detail.receipt.date).format('DD MMM YYYY'),
				thumbnail: detail.receipt.thumbnail,
				currency: detail.receipt.currency,
				value: detail.receipt.value,
				has_tax: detail.receipt.has_tax
			};

			new_detail.job = detail.job;
			new_detail.phase = detail.phase;
			new_detail.kind = detail.kind;
			new_detail.amount = 1.0 * detail.amount; // these are type decimal, stored as character in the json.
		});
	}

	// set our supplier by retrieving the details from our API
	this.set_supplier = function(supplier_id) {
		return new Promise((resolve, reject) => {
			let url = "/api/addressbook/" + supplier_id;
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(d => {
					let json = d.data;

					if (json.status == "OK") {
						// console.log("Supplier: ", json.data);

						this.supplier = json.data;
						this.country = json.data.country;
						this.currency = json.data.currency;


						resolve();
					} else {
						reject(json.reason);
					}					
				})
				.catch(function(e) {
					reject(e.message);
				});
		});
	}

	// our supplier property to get access to our supplier info
	Object.defineProperty(this, 'supplier', {
		get() {
			return this._supplier;
		},

		set(supplier) {
			this._supplier.id = supplier.id;
			this._supplier.display_name = supplier.display_name;
		}
	})

	// our country property to get access to our country info, setting this will also update currency data
	Object.defineProperty(this, 'country', {
		get() {
			return this._country;
		},

		set(country) {
			this._country.id = country.id;
			this._country.display_name = country.display_name;
			this._country.tax_perc = country.tax_perc;

			// apply new tax percentage to details
			this.details.forEach(detail => {
				detail.convert_currency();
			})
		}
	})

	// our date property, setting this will also update currency data
	Object.defineProperty(this, 'date', {
		get() {
			return moment(this._date).format("YYYY-MM-DD")
		},
		set(date) {
			// TODO validate this is a valid date!

			this._date = new Date(date);
			// console.log("New date",this._date);

			// check exchange rates
			this._get_exchange_rate(this._currency.id)
				.then(exrate => {
					// need to add some protection here in case we retrieve an old result.
					this._currency.exrate = exrate.rate;

					this.details.forEach(detail => {
						detail.convert_currency();
					})					
				})
				.catch(msg => {
					// need to do something here...
					console.log(msg)
				});
		}
	})

	// our currency property, setting this will also update our exchange rates
	Object.defineProperty(this, 'currency', {
		get() {
			return this._currency;
		},

		set(currency) {
			// console.log("New currency: ", currency);

			this.currency.id = currency.id;
			this.currency.symbol = currency.symbol;
			this.currency.code = currency.code;

			// check exchange rates
			this._get_exchange_rate(currency.id)
				.then(exrate => {
					// need to add some protection here in case we retrieve an old result.
					this.currency.exrate = exrate.rate;

					this.details.forEach(detail => {
						detail.convert_currency();
					})					
				})
				.catch(msg => {
					// need to do something here...
					console.log(msg)
				});
		}
	})

	// get the exchange rate for a currency
	this._get_exchange_rate = function(currency_id) {
		return new Promise((resolve, reject) => {
			let url = "/api/config/currency/" + currency_id + "/exchange_rates/" + this.date;
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(d => {
					let json = d.data;

					if (json.status == "OK") {
						resolve(json.data);
					} else {
						reject(json.reason);
					}					
				})
				.catch(function(e) {
					reject(e.message);
				});
		})
	}

	// our approval by property
	Object.defineProperty(this, 'approval_by', {
		get() {
			return this._approval_by;
		},

		set(employee) {
			this._approval_by.id = employee.id;
			this._approval_by.display_name = employee.display_name;
		}
	})

	// get our submitted date correctly formatted
	this.submitted_date = function() {
		if (this._submitted_date) {
			return moment(this._submitted_date).format("YYYY-MM-DD")			
		} else {
			return '';
		}
	}

	// calculate the total value of our expense claim
	this.total_value = function() {
		let value = 0.00;

		this.details.forEach(detail => {
			value += (1.0 * detail.amount);
		});

		return value.toFixed(2);
	}

	// add a new receipt to our expense claim, this will add a new detail line
	this.add_receipt = function(receipt_id) {
		return new Promise((resolve, reject) => {
			let url = "/api/expenses/receipts/" + receipt_id;
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(d => {
					let json = d.data;

					if (json.status == "OK") {
						// add our record
						let new_detail = this.add_detail();

						// copy some data into our receipt
						let data = json.data;
						new_detail.receipt = {
							id: data.id,
							date: data.date,
							date_fmt: moment(data.date).format('DD MMM YYYY'),
							thumbnail: data.thumbnail,
							currency: data.currency,
							value: data.value,
							has_tax: data.has_tax
						};

						new_detail.description = data.desc;

						if (data.job) {
							new_detail.job.id = data.job.id;
							new_detail.job.code = data.job.code;
							new_detail.job.display_name = data.job.display_name;							
						}

						if (data.phase) {
							new_detail.phase.id = data.phase.id;
							new_detail.phase.code = data.phase.code;
							new_detail.phase.display_name = data.phase.display_name;							
						}

						if (data.kind) {
							new_detail.kind.id = data.kind.id;
							new_detail.kind.code = data.kind.code;
							new_detail.kind.display_name = data.kind.display_name;							
						}
					} else {
						reject(json.reason);
					}					
				})
				.catch(function(e) {
					reject(e.message);
				});
		});
	}

	// update the line in relation to this receipt with this new info
	this.update_receipt = function(receipt) {
		return new Promise((resolve, reject) => {
			let found = false;

			this.details.forEach(detail => {
				if (detail.receipt.hasOwnProperty('id')) {
					if (detail.receipt.id == receipt.id) {
						found = true;

						detail.receipt = {
							id: receipt.id,
							date: receipt.date,
							date_fmt: moment(receipt.date).format('DD MMM YYYY'),
							thumbnail: receipt.thumbnail,
							currency: receipt.currency,
							value: receipt.value,
							has_tax: receipt.has_tax
						};

						detail.description = receipt.desc;

						if (receipt.job) {
							detail.job.id = receipt.job.id;
							detail.job.code = receipt.job.code;
							detail.job.display_name = receipt.job.display_name;							
						}

						if (receipt.phase) {
							detail.phase.id = receipt.phase.id;
							detail.phase.code = receipt.phase.code;
							detail.phase.display_name = receipt.phase.display_name;							
						}

						if (receipt.kind) {
							detail.kind.id = receipt.kind.id;
							detail.kind.code = receipt.kind.code;
							detail.kind.display_name = receipt.kind.display_name;							
						}
					}
				}
			});

			if (!found) {
				reject("Couldn't locate detail for this receipt");
			}
		});
	}

	// list all expense claims for the given employee
	this.list = function(employees, statusses, approval_by) {
		return new Promise((resolve, reject) => {
			// build our url parameters
			let params = new Array();
			employees = "" + employees; // make sure this is a string
			if (employees.length > 0) {
				params.push("employees=" + employees)
			}
			statusses = "" + statusses; // make sure this is a string
			if (statusses.length > 0) {
				params.push("statusses=" + statusses)
			}
			approval_by = "" + approval_by; // make sure this is a string
			if (approval_by.length > 0) {
				params.push("approval_by=" + approval_by)
			}

			// build our url, we have to add url escaping here!
			let url = "/api/expenses/claims";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}
			
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(function(d) {
					let json = d.data;

					if (json.status == "OK") {
						// here we return our data instead of updateing our internal state
						resolve(json.data);
					} else {
						reject(json.reason);
					}
				})
				.catch(function(e) {
					reject(e.message);
				});
		});
	}

	// load an expense claim
	this.load = function(id) {
		let self = this;

		return new Promise((resolve, reject) => {
			let url = "/api/expenses/claims/" + id;

			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(function(d) {
					let json = d.data;

					if (json.status == "OK") {
						// parse our new data
						self.set(json.data);

						// console.log(self)

						resolve();
					} else {
						reject(json.reason);
					}
				})
				.catch(function(e) {
					reject(e.message);
				});
		})
	}

	// insert this expense claim into the database
	this.insert = function() {
		let self = this;

		return new Promise((resolve, reject) => {
			// build our url parameters
			let params = new Array();

			// build our url, we have to add url escaping here!
			let url = "/api/expenses/claims";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			let details = [];
			self.details.forEach(detail => {
				details.push({
					receipt_id: detail.receipt.id,
					amount: detail.amount,
				})
			})
			
			axios
				.post(url, {

					supplier_id: self._supplier.id,
					date: self._date,
					desc: self.description,
					currency_id: self._currency.id,
					approval_by: self._approval_by.id,
					is_submitted: false,
					status: 0,
					remark: '',
					details: details,

				}, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(function(d) {
					// this is the result of the last search we've started, lets process it.
					let json = d.data;

					if (json.status == "OK") {
						// parse our new data
						self.set(json.data);

						resolve(json.data);
					} else {
						// console.log(json.reason);
						reject(json.reason);
					}
				})
				.catch(e => {
					// this is the error for the last search we've started, lets show it.
					// console.log(e);
					reject(e.message);
				})
		});
	}
	
	// update the expense claim in our database
	this.update = function() {
		let self = this;

		return new Promise((resolve, reject) => {
			// build our url parameters
			let params = new Array();

			// build our url, we have to add url escaping here!
			let url = "/api/expenses/claims/" + self.id;
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}

			let details = [];
			self.details.forEach(detail => {
				details.push({
					receipt_id: detail.receipt.id,
					amount: detail.amount,
				})
			})
			
			axios
				.put(url, {

					supplier_id: self._supplier.id,
					date: self.date,
					desc: self.description,
					currency_id: self._currency.id,
					approval_by: self._approval_by.id,
					status: self.status,
					remark: self.remark,
					period_id: self.period.id,
					send_email: self.email,
					details: details,

				}, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(d => {
					// this is the result of the last search we've started, lets process it.
					let json = d.data;

					// console.log("test")

					if (json.status == "OK") {
						// parse our new data
						self.set(json.data);

						resolve(json.data);
					} else {
						console.log(json.reason);
						reject(json.reason);
					}
				})
				.catch(e => {
					// this is the error for the last search we've started, lets show it.
					// console.log(e);
					reject(e.message);
				})
		});	
	}

	// delete this expense claim from our database
	this.delete = function() {
		let self = this;

		return new Promise((resolve, reject) => {
			axios
				.delete("/api/expenses/claims/" + self.id, {
				}, {
					headers: {
						"Authorization": "Bearer " + store.state.token,
						"Accept": "application/json"
					}
				})
				.then(function(d) {
					// Don't ask me why it does work here... 
					let json = d.data;
					
					if (json.status == "OK") {
						// self.set(json.data);
						resolve();
					} else {
						console.log(json.reason);
						reject(json.reason);
					}
				})
				.catch(e => {
					console.log(e);
					reject(e.message);
				})
		})
	};
}

export default Claim;